import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { AuthResponse, AuthUser } from "../types/auth";
import {
  AbschlussberichtDraft,
  AbschlussberichtSubmission,
  AbschlussberichtTemplate,
} from "../types/abschlussbericht";
import {
  ProjektdokumentationFormData,
  ProjektdokumentationStatusUpdate,
  ProjektdokumentationTemplate,
} from "../types/projektdokumentation";

type TypedAxiosInstance = Omit<
  AxiosInstance,
  "get" | "post" | "put" | "patch" | "delete"
> & {
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T>;
  put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T>;
  patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T>;
  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
};

import { getEnv } from "../utils/env";

// API-Basis-URL mit Fallbacks (unterstützt VITE_API_URL und VITE_API_BASE_URL)
const API_URL =
  getEnv("VITE_API_URL") ||
  getEnv("VITE_API_BASE_URL") ||
  "http://localhost:5175/api";

// Axios-Instanz mit Interceptors für Token
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000, // Hänger vermeiden: nach 30s abbrechen (erhöht für Uploads)
  headers: {
    "Content-Type": "application/json",
  },
}) as TypedAxiosInstance;

// Response-Interceptor für Fehlerbehandlung
api.interceptors.response.use(
  (response) => response.data,
  (error: AxiosError) => {
    // Fehlerdetails extrahieren
    // error.response?.data kann unterschiedlich geformt sein; sichere Extraktion des message-Feldes
    const respData = (error.response && (error.response as any).data) || {};
    const isGoogleAuthRequired =
      respData?.code === "GOOGLE_AUTH_REQUIRED" ||
      respData?.message === "Kein Google-Token verfügbar";

    if (error.response?.status === 401 && !isGoogleAuthRequired) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    const errorMsg =
      (respData && respData.message) ||
      error.message ||
      "Ein unerwarteter Fehler ist aufgetreten";

    return Promise.reject(new Error(errorMsg));
  }
);

// Auth API
export const login = async (
  email: string,
  password: string
): Promise<AuthResponse> => {
  return api.post<AuthResponse>("/auth/login", { email, password });
};

export const register = async (
  name: string,
  email: string,
  password: string
): Promise<AuthResponse> => {
  return api.post<AuthResponse>("/auth/register", { name, email, password });
};

export const getProfile = async (): Promise<AuthUser> => {
  return api.get<AuthUser>("/auth/me");
};

export const loginGoogle = async (
  credential: string
): Promise<AuthResponse> => {
  // Google Login hat keinen Mock - nutzt immer echte API
  return api.post<AuthResponse>("/auth/google", { credential });
};

// Projekte API
export const getProjects = async (date?: string) => {
  const params = date ? { date } : undefined;
  return api.get("/projects", { params });
};

export const getProject = async (id: string) => {
  return api.get(`/projects/${id}`);
};

export const getAbschlussberichtTemplate = async (
  projectId: string
): Promise<AbschlussberichtTemplate> => {
  return api.get(`/projects/${projectId}/abschlussbericht-template`, {
    timeout: 90000,
  });
};

export const submitAbschlussbericht = async (
  projectId: string,
  payload: AbschlussberichtSubmission
) => {
  return api.post(`/projects/${projectId}/abschlussbericht-submissions`, payload);
};

export const upsertAbschlussberichtDraft = async (
  projectId: string,
  payload: AbschlussberichtDraft
) => {
  return api.put(`/projects/${projectId}/abschlussbericht-draft`, payload);
};

export interface VotReportEntry {
  section: string;
  question: string;
  answer: string;
}

export interface VotReportPayload {
  projectId: string;
  dealName?: string;
  submittedAt: string;
  submittedByEmail?: string;
  entries: VotReportEntry[];
}

export interface VotReportResponse {
  success: boolean;
  dealName: string;
  targetDirectories: string[];
  file: {
    fileName: string;
    mimeType: string;
    base64Content: string;
  };
}

const getDevAccessToken = (): string | null =>
  localStorage.getItem("dev_access_token") || getEnv("VITE_DEV_ACCESS_TOKEN") || null;

export const submitVotReport = async (
  projectId: string,
  payload: VotReportPayload
): Promise<VotReportResponse> => {
  const devAccessToken = getDevAccessToken();
  return api.post(`/projects/${projectId}/vot-report`, payload, {
    timeout: 90000,
    withCredentials: true,
    headers: devAccessToken ? { Authorization: `Bearer ${devAccessToken}` } : undefined,
  });
};

export const getProjektdokumentationTemplate = async (
  projectId: string
): Promise<ProjektdokumentationTemplate> => {
  return api.get(`/projects/${projectId}/projektdokumentation-template`);
};

export const upsertProjektdokumentation = async (
  projectId: string,
  payload: ProjektdokumentationFormData
) => {
  return api.put(`/projects/${projectId}/projektdokumentation`, payload);
};

export const updateProjektdokumentationStatus = async (
  projectId: string,
  payload: ProjektdokumentationStatusUpdate
) => {
  return api.post(`/projects/${projectId}/projektdokumentation/status`, payload);
};

export const resolveProjectDriveFolder = async (
  projectId: string,
  path: string
): Promise<{ success: boolean; found: boolean; folderId: string; url: string; matchedPath: string }> => {
  return api.get(`/projects/${projectId}/drive-folder/resolve`, {
    params: { path },
  });
};

export const createProject = async (projectData: {
  name: string;
  kunde: string;
  adresse: string;
  anlagentyp: string;
  projektStatus?: string;
}) => {
  return api.post("/projects", projectData);
};

export const updateProject = async (id: string, projectData: any) => {
  return api.put(`/projects/${id}`, projectData);
};

export const deleteProject = async (id: string) => {
  return api.delete(`/projects/${id}`);
};

// Phasen API
export const getPhases = async (projectId: string) => {
  return api.get(`/phases/project/${projectId}/phases`);
};

export const getPhase = async (id: string) => {
  return api.get(`/phases/${id}`);
};

export const createPhase = async (projectId: string, phaseData: any) => {
  return api.post(`/phases/project/${projectId}/phases`, phaseData);
};

export const updatePhase = async (id: string, phaseData: any) => {
  return api.put(`/phases/${id}`, phaseData);
};

export const deletePhase = async (id: string) => {
  return api.delete(`/phases/${id}`);
};

// Checklisten-Item API
export const updateChecklistItem = async (
  phaseId: string,
  itemId: string,
  itemData: any
) => {
  return api.patch(`/phases/${phaseId}/checklist/${itemId}`, itemData);
};

// Dokumenttypen API
export const getDocumentTypes = async (phaseId?: string) => {
  const params = phaseId ? { phaseId } : undefined;
  return api.get("/document-types", { params });
};

// Export der API-Instanz für benutzerdefinierte Aufrufe
export default api;
